<template>
  <LcCard>
    <section class="flex items-center justify-between">
      <div class="radio-group flex items-center">
        <strong>{{ $t('account.payout.accounts.bankaccount.add') }}</strong>
      </div>

      <PaymentMethodSymbol variant="Bank" />
    </section>

    <LcForm
      ref="form"
      class="w-full mt-200"
      v-bind="{ validationSchema }"
      :initial-values="INITIAL_VALUES"
      :tracking="{ containerId: 'add_bank_account_form' }"
      @submit="saveBankAccount"
    >
      <LcFormField v-slot="{ field }" name="holderName">
        <LcInput
          v-bind="field"
          disabled
          :label="$t('account.payout.accounts.accountholder')"
          :value="holderName"
        />
      </LcFormField>

      <LcFormField v-slot="{ field }" mask="#########" name="routingNumber">
        <LcInput
          v-bind="field"
          :help-message="
            isRoutingNumberKnown === false
              ? $t('account.payout.input.routingnumber.hint')
              : undefined
          "
          :label="$t('account.payout.accounts.routingnumber')"
          @update:value="(routingNumber) => setBankName(routingNumber)"
        />
      </LcFormField>

      <LcFormField v-slot="{ field }" name="bankName">
        <LcInput
          v-bind="field"
          v-model:value="bankName"
          :disabled="isBankNameDisabled"
          :label="$t('account.payout.input.bankname.label')"
        />
      </LcFormField>

      <LcFormField v-slot="{ field }" name="accountNumber">
        <LcInput
          :label="$t('account.payout.accounts.accountnumber')"
          v-bind="field"
          mask="###################"
          @click.right.prevent
          @copy.prevent
          @paste.prevent
        >
          <template v-if="isAccountNumberValid" #suffix>
            <LcIcon class="ml-100 icon-accent-success-success" name="checkmark" />
          </template>
        </LcInput>
      </LcFormField>

      <LcFormField v-slot="{ field }" name="confirmAccountNumber">
        <LcInput
          :label="$t('account.payout.input.confirmaccountnumber.label')"
          v-bind="field"
          mask="###################"
          @click.right.prevent
          @copy.prevent
          @paste.prevent
        >
          <template v-if="isAccountNumberValid" #suffix>
            <LcIcon class="ml-100 icon-accent-success-success" name="checkmark" />
          </template>
        </LcInput>
      </LcFormField>

      <i18n-t keypath="account.payout.accounts.bankaccount.text" scope="global" tag="span">
        <template #contactSupport>
          <HyperLink class="inline-block text-link" :to="$localePath('/service?chat=true')">
            {{ $t('common.sentences.contactsupport').toLowerCase() }}
          </HyperLink>
        </template>
      </i18n-t>

      <LcButton class="w-full mt-200" :loading="isLoading" type="submit">
        {{ $t('account.payout.accounts.bankaccount.button.save') }}
      </LcButton>
    </LcForm>
  </LcCard>
</template>

<script lang="ts" setup>
import type { LcInputProps } from '@lottocom/frontend-components'
import type { ComponentExposed } from 'vue-component-type-helpers'

import * as Yup from 'yup'

import { PayOutAccountType } from '#api/shared/@types/generated/backend/graphql-schema-types'
import LcForm from '#tracking/app/component-overrides/LcForm.vue'

type AddBankAccountForm = {
  accountNumber: string
  bankName?: string
  confirmAccountNumber: string
  holderName?: string
  routingNumber: string
}

const INITIAL_VALUES = {
  accountNumber: '',
  bankName: '',
  confirmAccountNumber: '',
  holderName: '',
  routingNumber: '',
}

const { t } = useI18n()

const licensedTerritory = useLicensedTerritory({ raw: true })
const formRef = useTemplateRef<ComponentExposed<typeof LcForm>>('form')

const { log } = useDatadog()
const { getBankName } = useBankName()
const { addToastError } = useToaster()
const { addPayOutInstrument } = usePayoutSingleton()

const isUseManualAccountsOnly = inject('isUseManualAccountsOnly', ref(false))

const { result } = usePayOutAccountsQuery(
  { licensedTerritory, useManualAccountsOnly: isUseManualAccountsOnly.value },
  { fetchPolicy: 'cache-only' },
)

const validationSchema = Yup.object<Partial<AddBankAccountForm>>().shape({
  accountNumber: Yup.string().required(),
  bankName: Yup.string().test(
    'required',
    () => t('account.payout.input.bankname.required'),
    (value) => (!isBankNameDisabled.value ? !!value : true),
  ),
  confirmAccountNumber: Yup.string()
    .required()
    .oneOf([Yup.ref('accountNumber')], t('account.payout.input.confirmaccountnumber.validation')),
  holderName: Yup.string(),
  routingNumber: Yup.string().required().max(9).min(9),
})

const bankName = ref<string>()
const isRoutingNumberKnown = ref<boolean>()
const isLoading = ref(false)

const holderName = computed(() => result.value?.payOutAccounts.holderName || '')

const isAccountNumberValid = computed(
  () =>
    formRef.value?.internal?.isFieldValid('accountNumber') &&
    formRef.value?.internal?.isFieldValid('confirmAccountNumber'),
)

const isBankNameDisabled = computed(() => !(isRoutingNumberKnown.value === false))

const saveBankAccount = async ({ accountNumber, routingNumber }: AddBankAccountForm) => {
  try {
    isLoading.value = true
    await addPayOutInstrument(PayOutAccountType.Bank, [
      { key: 'accountNumber', value: accountNumber },
      { key: 'routingNumber', value: routingNumber },
    ])
  } catch (error) {
    log('error', {
      component: 'PayoutNewMethodBank.vue',
      message: 'Failed to save payout instrument',
      messageContext: { error },
    })

    addToastError(
      {
        error,
        prefix: 'account.payout.toast.error',
        tracking: {
          action: 'payout',
          topicKey: 'add_payout_instrument_failed',
        },
      },
      {
        fallback: {
          message: {
            translationKey: 'account.payout.toast.error.addpayoutinstrument.fallback.message',
            translationVariables: {
              contactSupport: {
                text: t('common.sentences.contactsupport').toLowerCase(),
                to: '/service?chat=true',
                type: 'link',
              },
            },
          },
          type: 'danger',
        },
      },
    )
  } finally {
    isLoading.value = false
  }
}

const setBankName = async (routingNumber: LcInputProps['value']) => {
  isRoutingNumberKnown.value = undefined
  formRef.value?.internal?.resetField('bankName')

  const isRoutingNumberValid = await validateRoutingNumber(routingNumber)

  if (!isRoutingNumberValid) {
    bankName.value = ''
    return
  }

  if (!routingNumber) return

  const response = await getBankName(String(routingNumber))
  bankName.value = response.bankName || ''
  isRoutingNumberKnown.value = response.isKnown
}

const validateRoutingNumber = (routingNumber: LcInputProps['value']) =>
  validationSchema.validateAt('routingNumber', { routingNumber }).then(
    () => true,
    () => false,
  )
</script>
